import * as PIXI from 'pixi.js';
import { useMapStore } from '../stores/mapStore';

/**
 * IsoGroundPlane
 *
 * Renders the battle map texture as an isometric ground plane.
 * The flat 2D map texture is projected onto the isometric diamond shape
 * using Pixi.js mesh with custom UV mapping.
 *
 * For Phase 1 we use a simpler approach: skew the sprite to approximate
 * the isometric ground angle (a rotation + scale transform).
 *
 * The ground plane occupies the full grid area in isometric space.
 */

const PLACEHOLDER_COLOR = 0x1a1410;
const GRID_TINT = 0x2a2420;

export class IsoGroundPlane {
  public container: PIXI.Container;

  private sprite: PIXI.Sprite | null = null;
  private bakedTexture: PIXI.Texture | null = null;
  private placeholder: PIXI.Graphics;
  private unsubscribe: (() => void) | null = null;
  private loadGeneration = 0;
  private currentTextureUrl: string | null = null;

  private gridWidth: number;
  private gridHeight: number;
  private tileWidth: number;
  private tileHeight: number;

  constructor(
    gridWidth: number,
    gridHeight: number,
    tileWidth: number,
    tileHeight: number
  ) {
    this.container = new PIXI.Container();
    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;

    // Create placeholder ground (dark parchment diamond)
    this.placeholder = new PIXI.Graphics();
    this.container.addChild(this.placeholder);
    this.drawPlaceholder();

    // Subscribe to map store for texture changes
    this.subscribeToStore();
  }

  private drawPlaceholder(): void {
    const g = this.placeholder;
    g.clear();

    // Draw filled diamond for the ground area
    const w = this.gridWidth;
    const h = this.gridHeight;

    // Four corners of the isometric grid
    const top = this.gridToScreen(0, 0);
    const right = this.gridToScreen(w, 0);
    const bottom = this.gridToScreen(w, h);
    const left = this.gridToScreen(0, h);

    // Dark ground fill
    g.beginFill(PLACEHOLDER_COLOR, 1);
    g.moveTo(top.x, top.y);
    g.lineTo(right.x, right.y);
    g.lineTo(bottom.x, bottom.y);
    g.lineTo(left.x, left.y);
    g.closePath();
    g.endFill();

    // Subtle diamond border
    g.lineStyle(2, GRID_TINT, 0.6);
    g.moveTo(top.x, top.y);
    g.lineTo(right.x, right.y);
    g.lineTo(bottom.x, bottom.y);
    g.lineTo(left.x, left.y);
    g.closePath();
  }

  private gridToScreen(gx: number, gy: number): { x: number; y: number } {
    const halfW = this.tileWidth / 2;
    const halfH = this.tileHeight / 2;
    return {
      x: (gx - gy) * halfW,
      y: (gx + gy) * halfH,
    };
  }

  /** Pick whichever map actually has a texture; prefer the iso slot if both do. */
  private getIsoMap() {
    const state = useMapStore.getState();
    const iso = state.activeIsoMap;
    const top = state.activeMap;
    if (iso?.baseTexture?.colorUrl) return iso;
    if (top?.baseTexture?.colorUrl) return top;
    return iso ?? top;
  }

  private subscribeToStore(): void {
    let lastUrl: string | null = null;
    this.unsubscribe = useMapStore.subscribe((state) => {
      const map = state.activeIsoMap?.baseTexture?.colorUrl
        ? state.activeIsoMap
        : state.activeMap?.baseTexture?.colorUrl
          ? state.activeMap
          : null;
      const nextUrl = map?.baseTexture?.colorUrl ?? null;
      if (nextUrl !== lastUrl) {
        console.log(`[IsoGround] Texture change: ${lastUrl ? 'had' : 'no'} → ${nextUrl ? 'loaded' : 'cleared'} (${map?.name ?? 'none'})`);
        lastUrl = nextUrl;
        this.loadTexture(nextUrl);
      }
    });

    // Load initial texture
    const map = this.getIsoMap();
    const initialUrl = map?.baseTexture?.colorUrl ?? null;
    if (initialUrl) {
      lastUrl = initialUrl;
      this.loadTexture(initialUrl);
    }
  }

  private async loadTexture(url: string | null): Promise<void> {
    const gen = ++this.loadGeneration;

    if (!url) {
      this.clearSprite();
      this.placeholder.visible = true;
      return;
    }

    if (url === this.currentTextureUrl) return;

    try {
      const sourceTexture = await PIXI.Texture.fromURL(url);

      // Stale check
      if (gen !== this.loadGeneration) return;

      const mapStyle = this.getIsoMap()?.style;
      if (mapStyle !== 'isometric') {
        console.warn(
          '[IsoGroundPlane] Active map is not iso-native — refusing to warp. ' +
          'Regenerate the map with iso style to render.'
        );
        this.clearSprite();
        this.currentTextureUrl = url;
        this.placeholder.visible = true;
        return;
      }

      // Bake the radial alpha falloff INTO a new texture at load time
      // instead of running a runtime mask. A sprite-mask + filter stack on
      // a 1440² texture allocates ~32MB/frame in render targets; baking is
      // a one-time cost that kills that recurring allocation.
      const baked = await IsoGroundPlane.bakeFeatheredTexture(sourceTexture);
      if (gen !== this.loadGeneration) {
        baked.destroy(true);
        return;
      }

      this.clearSprite();
      this.currentTextureUrl = url;
      this.bakedTexture = baked;

      const spr = new PIXI.Sprite(baked);
      this.sprite = spr;
      this.applyNativeIsoScale(spr);

      // Insert behind placeholder
      this.container.addChildAt(spr, 0);
      this.placeholder.visible = false;
    } catch {
      if (gen !== this.loadGeneration) return;
      console.warn('[IsoGroundPlane] Failed to load texture:', url);
      this.placeholder.visible = true;
    }
  }

  /**
   * Paint the FLUX texture onto a canvas with a radial alpha gradient
   * applied via 'destination-in' composite, then return a new Texture.
   * One-time cost per map load; no mask or filter needed at render time.
   */
  private static async bakeFeatheredTexture(src: PIXI.Texture): Promise<PIXI.Texture> {
    const img = src.baseTexture.resource;
    // Pixi wraps the image in a resource — pull out the underlying element.
    const source = (img as unknown as { source?: HTMLImageElement | HTMLCanvasElement }).source;
    const w = src.width;
    const h = src.height;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx || !source) return src; // fallback — render unfeathered

    // 1. Draw the source image
    ctx.drawImage(source as CanvasImageSource, 0, 0, w, h);

    // 2. Multiply by a radial alpha gradient (opaque center, transparent edges)
    ctx.globalCompositeOperation = 'destination-in';
    const cx = w / 2;
    const cy = h / 2;
    const inner = Math.min(w, h) * 0.35;
    const outer = Math.min(w, h) * 0.5;
    const grad = ctx.createRadialGradient(cx, cy, inner, cx, cy, outer);
    grad.addColorStop(0, 'rgba(0,0,0,1)');
    grad.addColorStop(0.7, 'rgba(0,0,0,0.9)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    return PIXI.Texture.from(canvas);
  }

  /**
   * Scale a natively isometric texture to fill the grid diamond.
   * No rotation/skew — the image is already iso-perspective.
   */
  private applyNativeIsoScale(spr: PIXI.Sprite): void {
    // The iso grid diamond has width = (gridWidth + gridHeight) * tileWidth / 2
    // and height = (gridWidth + gridHeight) * tileHeight / 2
    const diamondW = (this.gridWidth + this.gridHeight) * (this.tileWidth / 2);
    const diamondH = (this.gridWidth + this.gridHeight) * (this.tileHeight / 2);

    spr.width = diamondW;
    spr.height = diamondH;

    // Center the sprite on the grid diamond's top corner
    spr.anchor.set(0.5, 0);
    spr.position.set(0, -diamondH * 0.05); // slight upward nudge for visual alignment
  }

  private clearSprite(): void {
    if (this.sprite) {
      this.container.removeChild(this.sprite);
      this.sprite.destroy({ texture: false });
      this.sprite = null;
    }
    // Free the baked texture — it's unique per load, not shared in the
    // Pixi cache, so we own destroying it. Leaving them behind would leak
    // ~16MB per generation.
    if (this.bakedTexture) {
      this.bakedTexture.destroy(true);
      this.bakedTexture = null;
    }
    this.currentTextureUrl = null;
  }

  refresh(): void {
    const map = this.getIsoMap();
    this.loadTexture(map?.baseTexture?.colorUrl || null);
  }

  destroy(): void {
    this.unsubscribe?.();
    this.clearSprite();
    this.placeholder.destroy();
    this.container.destroy({ children: true });
  }
}
